import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, useTheme } from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import UserTable from "../../components/table/UserTable";
import PageContainer from "../../components/layout/PageContainer";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";

import { useGetUsersQuery } from "../../store/apis/usersApi";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

const Users: React.FC = () => {
  const navigate = useNavigate();
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: userResponse, isLoading, error } = useGetUsersQuery();

  useEffect(() => {
    if (error) {
      dispatch(
        setToastMessage({
          message: t("users.errors.fetchError", {
            error: "data" in error ? error.data : t("common.unknownError"),
          }),
          severity: "error",
        })
      );
    }
  }, [error, dispatch, t]);

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h4" gutterBottom>
                {t("users.users")}
              </Typography>
              <Typography variant="pSmall">{t("users.editOrAdd")}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate("/users/new")}
                sx={{
                  width: "100%",
                  height: "100%",
                  padding: 1.5,
                  backgroundColor: theme.palette.primary.main,
                  boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
                  borderRadius: "50px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                <AddIcon
                  sx={{
                    fontSize: 24,
                    color: theme.palette.primary.contrastText,
                  }}
                />
                <Typography
                  variant="pMedium"
                  sx={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: 700,
                    fontFamily: "Nunito",
                  }}
                >
                  {t("users.new")}
                </Typography>
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <OrbLoader
                  size="large"
                  label={t("common.loading", { defaultValue: "Cargando…" })}
                />
              </Box>
            ) : (
              <UserTable
                usersData={userResponse?.data ?? []}
                maxHeight="none"
              />
            )}
          </Box>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default Users;
